'use client';

import { ElementType, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.js';
import { Link } from '@sola/ark-core/atoms/link/link.js';
import { getArticlePath } from '@sola/contentful-model/lib/article.js';
import { ArticleTypesEnum } from '@sola/contentful-model/lib/taxonomy-values.js';
import { slugify, trailingSlash } from '@sola/utils/string.js';

export const ArticleTabsEnum = z.enum([
  'all',
  'topic',
  'theme',
  'country',
  'search',
]);
export type ArticleTabsEnum = z.infer<typeof ArticleTabsEnum>;

export const ArticleTermListProps = z.object({
  terms: z.array(z.object({ path: z.string(), name: z.string() })),
  group: ArticleTabsEnum, // todo: don't do this in zod 4
});

export type ArticleTermListProps = z.infer<typeof ArticleTermListProps>;

type FacetType = (
  | {
      field: string;
      values: {
        key: string;
        count: number;
      }[];
    }
  | undefined
)[];
type TaxonomyFacets = {
  topic?: FacetType;
  theme?: FacetType;
  country?: FacetType;
};
type ArticleTermListTypes = {
  seoLevel: number;
  tabGroup: ArticleTabsEnum;
  articleType: ArticleTypesEnum;
  facets: TaxonomyFacets;
};

export const ArticleTermList: React.FC<ArticleTermListTypes> = ({
  seoLevel = 3,
  articleType,
  facets,
}) => {
  const pathname = getArticlePath('tag', articleType);

  const { t } = useTranslation(['geo', 'skippy']);

  const terms = useMemo(() => {
    const parsed: { path: string; name: string }[] = [];
    Object.keys(facets).forEach((key) => {
      const taxonomyKey = key as keyof typeof facets;
      if (facets[taxonomyKey]) {
        facets[taxonomyKey][0]?.values.forEach((term) => {
          let termName = '';
          if (key === 'country') {
            const country = term.key.toUpperCase();
            termName = t(`geo:countries.${country}.nameS`);
          }
          if (key !== 'country') {
            termName =
              key === 'theme'
                ? t(
                    `articles:taxonomy.articleThemes.${term.key}.name`,
                    t(`articles:taxonomy.all`),
                  )
                : t(
                    `articles:taxonomy.articleTopics.${term.key}.name`,
                    t(`articles:taxonomy.all`),
                  );
          }
          parsed.push({
            path: `${trailingSlash(pathname)}${slugify(termName)}/`,
            name: termName,
          });
        });
      }
    });

    return parsed?.sort((a, b) => a.name.localeCompare(b.name));
  }, [facets]);

  const Comp = `h${seoLevel}` as ElementType;

  return (
    <ul className="ArticleTermList m-none p-none gap-comfy-lg grid grid-cols-1 md:grid-cols-3 md:grid-flow-col">
      {terms.map((term) => {
        return (
          <li className="ArticleTermList__item" key={term.path}>
            <LinkStyle noUnderline size="lg" purpose="neutral" noPadding>
              <Link href={term.path} title={term.name}>
                <Comp className="label-potent body-md">{term.name}</Comp>
              </Link>
            </LinkStyle>
          </li>
        );
      })}
    </ul>
  );
};
